import { useTranslation } from 'react-i18next'
import { Smartphone } from 'lucide-react'
import type { ListingAuditReport } from '@/lib/api/listing-builder.types'
import { SectionCard } from '@/components/v2/tools/amazon-listing/shared/SectionCard'
import { RecommendationCards } from '@/components/v2/tools/amazon-listing/shared/RecommendationCards'
import { SalesPsychologyCard } from '@/components/v2/tools/amazon-listing/shared/SalesPsychologyCard'
import { TitleLengthBar } from '@/components/v2/tools/amazon-listing/shared/TitleLengthBar'

interface TitleAnalysisProps {
  analysis: ListingAuditReport['title_analysis']
}

export function TitleAnalysis({ analysis }: TitleAnalysisProps) {
  const { t } = useTranslation()
  if (!analysis) return null
  const r = (key: string) => t(`amazonListing.report.${key}`)
  const mc = analysis.market_comparison

  return (
    <div className="space-y-4">
      <SectionCard title={r('titleAnalysis')} score={analysis.score}>
        <div className="space-y-2">
          {analysis.criteria?.map((c) => (
            <CriteriaRow key={c.name} c={c} t={t} />
          ))}
        </div>
      </SectionCard>

      {mc?.source_length > 0 && (
        <TitleLengthBar
          source={mc.source_length}
          avg={mc.avg_competitor_length}
        />
      )}

      {analysis.mobile_preview && (
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Smartphone className="w-4 h-4 text-neutral-500" />
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{r('mobilePreview')}</span>
          </div>
          <p className="text-sm text-neutral-900 dark:text-white font-mono bg-white dark:bg-neutral-900 p-3 rounded-lg">
            {analysis.mobile_preview}
          </p>
        </div>
      )}

      {analysis.sales_psychology && <SalesPsychologyCard data={analysis.sales_psychology} />}
      {analysis.recommendations?.length > 0 && <RecommendationCards recs={analysis.recommendations} t={t} />}

      {analysis.missing_competitor_keywords?.length > 0 && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 p-4">
          <h4 className="text-sm font-medium mb-2">{r('missingKeywords')}</h4>
          <div className="flex flex-wrap gap-1.5">
            {analysis.missing_competitor_keywords.map((kw) => (
              <span key={kw} className="px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">{kw}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function scoreToGrade(score: number): { letter: string; color: string; textColor: string } {
  if (score >= 85) return { letter: 'A', color: 'bg-green-700', textColor: 'text-green-700 dark:text-green-400' }
  if (score >= 70) return { letter: 'B', color: 'bg-green-500', textColor: 'text-green-600 dark:text-green-400' }
  if (score >= 55) return { letter: 'C', color: 'bg-yellow-500', textColor: 'text-yellow-600 dark:text-yellow-400' }
  if (score >= 40) return { letter: 'D', color: 'bg-orange-500', textColor: 'text-orange-600 dark:text-orange-400' }
  if (score >= 20) return { letter: 'E', color: 'bg-red-500', textColor: 'text-red-500 dark:text-red-400' }
  return { letter: 'F', color: 'bg-red-800', textColor: 'text-red-700 dark:text-red-400' }
}

function CriteriaRow({ c, t }: { c: { name: string; score: number; max: number }; t: (key: string) => string }) {
  const pct = c.max > 0 ? (c.score / c.max) * 100 : 0
  const grade = scoreToGrade(pct)
  const label = t(`amazonListing.report.titleCriteria.${c.name}`)
  return (
    <div className="flex items-center gap-3">
      <span className="w-24 sm:w-40 text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 truncate">{label}</span>
      <div className="flex-1 h-2 bg-neutral-100 dark:bg-neutral-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${grade.color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-sm font-bold w-10 text-right ${grade.textColor}`}>{Math.round(pct)}</span>
    </div>
  )
}
